import { Injectable, OnDestroy } from '@angular/core';
import * as d3 from 'd3';
import { isNil, uniqBy } from 'lodash';
import { EfficiencyAnalysisApiService } from '@app/shared/services/api/efficiency-analysis-api.service';
import { EfficiencyAnalysisColorStatus } from '@app/shared/enums';
import { EfficiencyTresholdService } from '@app/efficiency-analysis/services/efficiency-treshold.service';
import { EABuilding } from '@app/efficiency-analysis/models/buildings/efficiency-analysis-buildings';
import { EAChildBuildings, EABuildingDetailsAndChildren } from '../models/efficiency-analysis/building-details-children';
import { ClimateFactorsDto } from '../models/efficiency-analysis/climate-factors';
import { EfficiencyThreshold } from '@app/efficiency-analysis/models/thresholds/efficiency-threshold';
import { ComparisonValues } from '@app/efficiency-analysis/models/comparison-values';
import { Observable, Subject } from 'rxjs';
import { shareReplay, takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class EfficiencyAnalysisService implements OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();

  public buildingsWithEA: EABuildingDetailsAndChildren[] = [];
  public buildingsWithoutEA: EABuildingDetailsAndChildren[] = [];
  public climateFactors: ClimateFactorsDto[] = [];
  public hasEfficiencyAnalysis = false;
  public isRecalculated = false;
  public oldBuildingData: any[] = [];

  private efficient = 0;
  private inefficient = 0;
  private comparisonValuesArray: ComparisonValues[] = [];
  constructor(private efficiencyAnalysisApiService: EfficiencyAnalysisApiService, private efficiencyTresholdService: EfficiencyTresholdService) {
    this.setThresholds();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public setEfficiencyAnalysisStateByGuid(guid: string): void {
    this.comparisonValues$.subscribe((values: ComparisonValues[]) => {
      this.comparisonValuesArray = values;
      const isInComparison = this.comparisonValuesArray.find((val: ComparisonValues) => val.buildingTypeId === guid);
      this.hasEfficiencyAnalysis = !!isInComparison;
    });
  }

  public getEAColorStatusTriColor(efficiencyPercentage: number): EfficiencyAnalysisColorStatus {
    const green = EfficiencyAnalysisColorStatus.efficient;
    const yellow = EfficiencyAnalysisColorStatus.midEfficient;
    const red = EfficiencyAnalysisColorStatus.inefficient;

    const efficient = this.efficient;
    const inefficient = this.inefficient;

    switch (true) {
      case efficiencyPercentage >= 0 && efficiencyPercentage <= efficient:
        return green;
      case efficient < efficiencyPercentage && efficiencyPercentage < inefficient:
        return yellow;
      case (inefficient <= efficiencyPercentage && efficiencyPercentage <= 100) || efficiencyPercentage > 100:
      default:
        return red;
    }
  }

  public getEfficiencyPercentage(calculatedEfficiency: number, minRange: number, maxRange: number): number {
    try {
      const xScale = d3.scaleLinear().domain([minRange, maxRange]).range([0, 100]);
      const result = xScale(calculatedEfficiency);
      return result > 100 ? 100 : parseInt(result.toFixed(0), 10);
    } catch {
      return 0;
    }
  }

  public getAddress = (loc: any): string =>
    isNil(loc) ? '' : loc.street.concat(' ', loc.houseNumber || ',', '\r\n', loc.zipCode || '', ' ', loc.district || '');

  public mapBuildingData(buildings: EABuilding[], uniqueBuildings: EABuilding[]): EABuildingDetailsAndChildren[] {
    const groupedData: EABuildingDetailsAndChildren[] = [];
    uniqueBuildings.forEach((uniq: EABuilding) => {
      const newArray = {
        buildingTypeId: uniq.buildingTypeId,
        buildingTypeName: uniq.buildingType?.name,
      } as EABuildingDetailsAndChildren;
      groupedData.push(newArray);
    });

    for (const data of groupedData) {
      let children: EAChildBuildings[] = [];
      const buildingTypeId = data.buildingTypeId;

      for (const building of buildings) {
        if (building.buildingType?.guid === buildingTypeId) {
          this.populateChildrenArray(building, children);
        }
      }
      data.children = children;
    }
    groupedData.forEach((group: EABuildingDetailsAndChildren) => (group.children = uniqBy(group.children, (child: EAChildBuildings) => child.guid)));
    return groupedData;
  }

  private populateChildrenArray(building: EABuilding, children: EAChildBuildings[]): void {
    const child = {
      address: this.getAddress(building),
      buildingName: building.name ? building.name : building.buildingType?.name,
      buildingTypeId: building.buildingTypeId,
      buildingTypeName: building.buildingType?.name,
      district: building.district,
      efficiencyAnalysisDate: building.efficiencyAnalysisDate,
      efficiencyPercentageElectricity: building.efficiencyPercentageElectricity,
      efficiencyPercentageHeat: building.efficiencyPercentageHeat,
      electricityColorStatus: building.electricityColorStatus,
      electricityComputedValue: building.electricityComputedValue,
      guid: building.guid,
      heatColorStatus: building.heatColorStatus,
      heatComputedValue: building.heatComputedValue,
      houseNumber: building.houseNumber,
      notes: building.notes,
      street: building.street,
      zipCode: building.zipCode,
    } as EAChildBuildings;

    children.push(child);

    children = uniqBy(children, (child: EAChildBuildings) => child.guid);

    const sortBy = [
      { prop: 'buildingName', direction: 1 },
      { prop: 'address', direction: 1 },
    ];

    const compareByProps = (a: any, b: any, sortBy: any[]) => {
      for (const { prop, direction } of sortBy) {
        const aValue = a[prop].toString();
        const bValue = b[prop].toString();

        if (aValue < bValue) {
          return -1 * direction;
        } else if (aValue > bValue) {
          return 1 * direction;
        }
      }
      return 0;
    };

    children.sort((first: EAChildBuildings, second: EAChildBuildings) => compareByProps(first, second, sortBy));
  }

  private comparisonValues$: Observable<ComparisonValues[]> = this.efficiencyAnalysisApiService
    .getComparisonValues()
    .pipe(takeUntil(this.unsubscribe$), shareReplay({ bufferSize: 1, refCount: true }));

  private threshold$: Observable<EfficiencyThreshold> = this.efficiencyTresholdService.treshold.pipe(
    takeUntil(this.unsubscribe$),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private setThresholds(): void {
    this.threshold$.subscribe((threshold: EfficiencyThreshold) => {
      this.efficient = threshold.efficientTreshold;
      this.inefficient = threshold.inefficientTreshold;
    });
  }
}
